import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { cancelAllPendingRequests } from '../Services/AxiosInterceptor';

/**
 * Hook that automatically cancels all pending API requests when 
 * the route (URL path) changes.
 * 
 * Place this once inside a component that has access to React Router 
 * context (i.e., inside <BrowserRouter>).
 * 
 * How it works:
 * - On every route change, all tracked in-flight Axios requests are aborted.
 * - The first render is skipped (we don't cancel on initial page load).
 * - Cancelled requests are silently ignored by WebService's axios.isCancel() checks.
 */
export function useRouteCancelRequests(): void {
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip cancellation on initial mount — we don't want to cancel
    // the very first API calls when the page loads.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Route changed — cancel all pending requests from the previous page
    cancelAllPendingRequests();
  }, [location.pathname]);
}

export default useRouteCancelRequests;
